export const SUPPLIER_ENTITY_TYPES = ['company', 'brand'] as const;
export type SupplierEntityType = (typeof SUPPLIER_ENTITY_TYPES)[number];

export type SupplierCountry = {
    id: number;
    name: string;
    code_a3: string;
    code_a2: string;
    flag: string;
};

/**
 * Where one `SupplierInfoEntry` came from — docs §2.6a. No endpoint of its own: this only ever
 * arrives nested inside an entity's `supplier_info`/`summary` arrays. One row is reused across
 * every entity a given provider (or a given editing user) has touched.
 */
export type InfoSource = {
    id: number;
    /** e.g. `"SV"`, `"Dun & Bradstreet"`, `"Mithra AI enrichment"`, or `"Manual edit (Jane Doe)"`. */
    name: string;
    /** Pipeline/algorithm version — `''` when unknown, always `''` for a manual edit. */
    version: string;
    /** When this source last produced or refreshed its value. */
    date: string | null;
};

/**
 * One source's contribution to an entity's `supplier_info` or `summary` (§2.6a). The underlying row
 * can carry both at once, but each array surfaces only the one field it is for — hence the generic
 * `value`: the two fields disagree on its shape (see the two aliases below).
 *
 * `is_manual` is derived server-side from `source` (true iff it points at a user), so it can never
 * drift out of sync with the source the entry actually has.
 */
export type InfoEntry<TValue> = {
    value: TValue;
    source: InfoSource;
    is_manual: boolean;
};

/** An entry of `supplier_info` — one blob of identity prose per source (§2.6a). */
export type SupplierInfoEntry = InfoEntry<string>;

/**
 * An entry of `summary` — one string per source, backing "Resolved identity" (§2.1, field-to-UI
 * mapping corrected 2026-10-05, architecture doc item 54).
 */
export type SupplierSummaryEntry = InfoEntry<string>;

/**
 * An entry of `goods_and_services` — **several strings per source**, not one (§2.1/§2.6a, added
 * 2026-10-05). One source can supply more than one distinct fact: D&B's `org.activities` is a list
 * of separately-described activities and every one is kept.
 */
export type SupplierGoodsAndServicesEntry = InfoEntry<string[]>;

/**
 * The "current" value of a `supplier_info`/`summary` array: the array is ordered manual-first, then
 * most-recently-sourced, so `[0]` is what the single-slot design field renders (§2.1, §3.3).
 */
export function currentInfoValue(entries: InfoEntry<string>[] | undefined): string {
    return entries?.[0]?.value ?? '';
}

/**
 * supplier_360/entity/ — docs/supplier-360-api-endpoints.md §2.1
 */
export type SupplierEntity = {
    id: number;
    /** Same value as `data_source.Supplier.s_id` for the anchor raw row — renamed from `se_id` (docs §2.1). */
    s_id: string;
    databag_id: number;
    canonical_name: string;
    entity_type: SupplierEntityType;
    confidence: number;
    se_city: string;
    se_country: SupplierCountry | null;
    se_region: string;
    se_context_1: string;
    se_context_2: string;
    se_context_3: string;
    /**
     * The whole cluster's spend (every `ClusterMember`'s own `s_id`), computed server-side on both
     * list and detail — added 2026-09-08, docs §2.1. `0`, never null.
     */
    spend: number;
    /**
     * Distinct `MatPartReviewRow.business_unit__bu_name` values across the whole cluster — added
     * 2026-09-08, docs §2.1. Not `SegmentationScore.business_unit`, which is a single-valued
     * segmentation input (§2.12). `[]`, never null.
     */
    business_units: string[];
    /**
     * Same-origin path to this entity's company logo (`GET supplier_360/entity/{id}/logo/`) — added
     * 2026-09-15, docs §2.1. `''` when there is no logo on file, and never a placeholder image, so
     * the FE owns its own fallback. A domain write resolves it before the edit response (§2.5);
     * the nightly `get_logo_from_brandfetch` command covers domains that have not changed since.
     * Read it as a flag only — the request is built from the entity id
     * (`supplierEntityLogoUrl`), since this value is rooted at the host, not at the API base URL.
     */
    logo_url: string;
    /**
     * Identity prose per source — added 2026-09-18, docs §2.1. Not rendered anywhere since the
     * 2026-10-05 mapping fix (the description reads `summary` now), but still writable via
     * `edit_info`. One entry per source, manual-first then most-recently sourced. `[]`, never null.
     */
    supplier_info: SupplierInfoEntry[];
    /**
     * The "Resolved identity" / "Supplier description" text — added 2026-09-18, mapping corrected
     * 2026-10-05, docs §2.1/§3.3. One entry per source (the AI job or a manual edit), manual-first
     * then most-recently sourced — take `[0]` as current (`currentInfoValue`). `[]`, never null.
     * Read-only here: the write path is `PATCH supplier_360/entity/{id}/edit_info/` with `{summary}`.
     */
    summary: SupplierSummaryEntry[];
    /**
     * The "Goods & services" section — added 2026-10-05, docs §2.1. Same ordering as `summary`, but
     * each entry's `value` is an **array** of strings; replaces the removed `ServiceMapping` chips.
     * Populated by the D&B loader (every `org.activities[].description`). Not editable via
     * `edit_info` yet. `[]`, never null.
     */
    goods_and_services: SupplierGoodsAndServicesEntry[];
    created_at: string;
    updated_at: string;
};

/** `data/source/` — §2.24. App-wide `data_processing` lookup, not a `supplier_360` endpoint. */
export type SupplierDataSource = {
    id: number;
    /** Stable identifier, e.g. `"SAP-UK"`. Unique. */
    code: string;
    /** Freely-editable display label — falls back to `code` when it was never relabelled. */
    name: string;
    notes: string;
};

export const SUPPLIER_CLUSTER_MEMBER_MATCH_BASES = ['vat', 'fuzzy_name', 'llm_confirmed', 'manual'] as const;
export type SupplierClusterMemberMatchBasis = (typeof SUPPLIER_CLUSTER_MEMBER_MATCH_BASES)[number];

/**
 * supplier_360/cluster_member/ — the "Raw view" registry rows, docs/supplier-360-api-endpoints.md §2.2
 */
export type SupplierClusterMember = {
    id: number;
    /** Nested since 2026-09-08 — the full parent entity, so "Resolves to" renders without a second call. */
    entity: SupplierEntity;
    databag_id: number;
    /** The source system's own supplier id. */
    s_id: string;
    /** The name this row was clustered under — frozen at cluster time, renamed from `raw_name` 2026-10-20 (docs §2.2). */
    normalized_name: string;
    /**
     * Live-joined against `data_source.Supplier` on `(databag_id, s_id)` (added 2026-10-20) — the
     * name as it is in the raw ERP data right now, independent of `normalized_name`. `''` when no
     * matching `Supplier` row exists (e.g. a manually-added row with a synthetic id), docs §2.2.
     */
    s_name: string;
    /**
     * Nested read-only `data_processing.DataSource` since CAT-2460 (2026-09-10), replacing the
     * bare `source_system_id` string this used to be. Write it with `data_source_id` — §2.2.
     */
    data_source: SupplierDataSource | null;
    /** Same nested-`Country` shape as `SupplierEntity.se_country` — added 2026-09-08. */
    country: SupplierCountry | null;
    vat: string;
    /** This one raw row's own spend, computed server-side — `0`, never null, when it has no transactions. */
    spend: number;
    match_basis: SupplierClusterMemberMatchBasis;
    flagged: boolean;
    flag_note: string;
    created_at: string;
};

export type SupplierDetailOutletContext = {
    entity: SupplierEntity;
};

export type SupplierEntityListParams = {
    /** Exact only — docs §2.1 */
    entity_type?: SupplierEntityType;
    /** Exact or multi (`se_country__code_a2` / `__in`) — ISO alpha-2, not the nested Country object, docs §2.1 */
    se_country_code_a2?: string | string[];
    /** Score range — `confidence__gte` / `confidence__lte`, docs §2.1 */
    confidence_gte?: number;
    confidence_lte?: number;
    /** Cluster-wide spend range — `spend__gte` / `spend__lte`, docs §2.1 */
    spend_gte?: number;
    spend_lte?: number;
    /**
     * `business_unit` — one param, a single name or a CSV list (no `__in` suffix), matched against
     * the whole cluster's transactions, docs §2.1.
     */
    business_unit?: string[];
    search?: string;
    /** DRF `?ordering=` — `-` prefix for descending, comma-separated for multi-sort. */
    ordering?: string;
    page: number;
    pageSize: number;
};

/** `supplier_360/cluster_member/` list params — docs §2.2 */
export type SupplierClusterMemberListParams = {
    /** Exact only */
    entity?: number;
    /** Exact only */
    s_id?: string;
    /** Column filter — `normalized_name__icontains`, alongside the page-level `search`, docs §2.2 */
    normalized_name_contains?: string;
    /** `DataSource` ids — exact or `data_source__in`, docs §2.2 */
    data_source?: number[];
    /** ISO alpha-2 — exact or `country__code_a2__in`, docs §2.2 */
    country_code_a2?: string[];
    /** `vat` exact */
    vat?: string;
    /** `vat__icontains` */
    vat_contains?: string;
    /** Exact or `match_basis__in`, docs §2.2 */
    match_basis?: SupplierClusterMemberMatchBasis[];
    /** This raw row's own spend range — `spend__gte` / `spend__lte`, docs §2.2 */
    spend_gte?: number;
    spend_lte?: number;
    /** Case-insensitive substring on `normalized_name` only — not a join to `Supplier.s_name`, docs §2.2 */
    search?: string;
    /** DRF `?ordering=` — `-` prefix for descending, comma-separated for multi-sort. */
    ordering?: string;
    page: number;
    pageSize: number;
};

/**
 * `POST supplier_360/cluster_member/` body — the "Add raw row" modal (§2.2).
 *
 * `databag_id`, `s_id` and `match_basis` are server-set and ignored if sent: a manually
 * added row gets a generated `manual-{hex}` id and `match_basis: 'manual'`. `spend` is computed
 * from the row's transactions, so a new row always starts at `0` and it is not writable either.
 */
export type SupplierClusterMemberCreate = {
    entity_id: number;
    normalized_name: string;
    /** An existing `DataSource` id (§2.24) — there is no "type a new system name in" path. */
    data_source_id?: number;
    /** A `cat_basics.Country` id, not an alpha-2 code. */
    country_id?: number;
    vat?: string;
};

// ── Enrichment profile — §2.5 ──

export type EnrichmentProfile = {
    id: number;
    entity: number;
    raw_extracted_text: string;
    /** Free-form JSON. Only `domain` is user-editable; the rest is loader-written. */
    contact_info: Record<string, string>;
    confidence: number;
    sources: unknown[];
    last_refreshed_at: string;
    /**
     * True once a user has edited `contact_info.domain` — the one field this model still exposes a
     * write path for. The D&B/AI-job loaders carry a locked field's value forward instead of
     * refreshing it, so a user's edit outranks a later automated pass (§2.5, 2026-09-14). There is
     * no unlock action.
     */
    domain_is_manual: boolean;
};

// ── Contacts — §2.23 ──

export const SUPPLIER_CONTACT_TYPES = ['relation_manager', 'internal_stakeholder'] as const;
/** Which of the Identity section's two multi-selects a contact belongs to. */
export type SupplierContactType = (typeof SUPPLIER_CONTACT_TYPES)[number];

/**
 * A `User` as nested on a contact (§2.23, re-modeled 2026-10-23 per the user) — only an EXISTING
 * Mithra account can be checked here, never a newly-invented person (GDPR; see backend's own
 * `SupplierContact` docstring). `Profile`/the "Supplier-side contact" type are gone; CAT-4064 tracks
 * bringing a real, PII-safe external-contact design back.
 */
export type SupplierContactUser = {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
};

/**
 * `supplier_360/contact/` — docs §2.23. The row itself *is* the checkmark: it says this `user`
 * counts as this `contact_type` for this entity. Unique per `(entity, contact_type, user)`, so one
 * person can hold two types on one entity but never appear twice in the same field. Deleting a row
 * unchecks the person, it never deletes them.
 */
export type SupplierContact = {
    id: number;
    entity: number;
    contact_type: SupplierContactType;
    /** Nested, read-only — written only via `user_id` on create. */
    user: SupplierContactUser;
    /**
     * Per-assignment, not on the user — the same person can hold a different role on another
     * supplier. Blank for most `relation_manager` rows: there the type is the role.
     */
    role: string;
    created_at: string;
};

/** How a contact is labelled in the multi-selects — `email` is the fallback, it is always set. */
export function supplierContactUserName(user: SupplierContactUser): string {
    return `${user.first_name} ${user.last_name}`.trim() || user.email;
}

// ── Relationships — §2.4 ──

export type SupplierRelationshipType = 'headquarters' | 'branch' | 'subsidiary' | 'reseller_partner';

export type SupplierRelationship = {
    id: number;
    from_entity: number;
    to_entity: number | null;
    to_name: string;
    relationship_type: SupplierRelationshipType;
    confidence: number;
    evidence_source: string;
};

/**
 * One `relationship/parent_names/` suggestion (§2.4) — a distinct parent name, **not** a
 * relationship row. `entity_id` is set only when some relationship recorded this name against a
 * real `SupplierEntity`; null means it has only ever been typed as free text.
 */
export type SupplierParentName = {
    name: string;
    entity_id: number | null;
};

// ── Compliance — BU qualification — §2.7 ──

export const BU_COMPLIANCE_STATUSES = ['compliant', 'expiring_soon', 'non_compliant', 'pending_review'] as const;
export type BuComplianceStatus = (typeof BU_COMPLIANCE_STATUSES)[number];

export const QUALIFICATION_STATUSES = ['approved', 'conditional', 'suspended'] as const;
export type QualificationStatus = (typeof QUALIFICATION_STATUSES)[number];

/** `data_source.BusinessUnit` as nested on a `BUQualification` (§2.7). */
export type ComplianceBusinessUnit = {
    id: number;
    databag_id: number;
    bu_id: string;
    bu_name: string;
};

export type BuQualification = {
    id: number;
    entity: number;
    /**
     * Nested `data_source.BusinessUnit` since 2026-10-13 (was a bare string); write it as
     * `business_unit_id`. Permanently nullable: the backfill left ambiguous rows `null`.
     */
    business_unit: ComplianceBusinessUnit | null;
    region: string;
    category: string;
    status: BuComplianceStatus;
    qualification_status: QualificationStatus;
    risk_class: string;
    requalification_due: string | null;
    approver: string;
    qualified_date: string | null;
};

/**
 * `supplier_360/bu_qualification/kpi_summary/?entity=` — §2.7. The Business units view's KPI strip:
 * `BUQualification` rows counted by their own `status`.
 */
export type BuQualificationKpiSummary = {
    count: number;
    compliant: number;
    expiring_soon: number;
    non_compliant: number;
    pending_review: number;
};

/**
 * One group row of `compliance_requirement/muix/grid/` with `groupFields=["bu"]` (§2.8a): a BU's
 * requirement counts. `id` is the `BUQualification` id as a string; `null` collects the company-wide
 * records linked to no BU.
 */
export type ComplianceBuAggregate = {
    id: string | null;
    group: string | null;
    count: number;
    total_agg: number;
    met_agg: number;
    expiring_agg: number;
    pending_agg: number;
    /** `missing` + `expired`. */
    unmet_agg: number;
};

// ── Compliance — requirements — §2.8 ──

export const COMPLIANCE_LAYERS = ['company_wide', 'business_unit'] as const;
export type ComplianceLayer = (typeof COMPLIANCE_LAYERS)[number];

export const COMPLIANCE_REQUIREMENT_STATES = ['met', 'missing', 'expired', 'expiring', 'pending'] as const;
export type ComplianceRequirementState = (typeof COMPLIANCE_REQUIREMENT_STATES)[number];

export const COMPLIANCE_REMINDER_STATES = ['scheduled', 'reminder_sent', 'final_reminder_sent', 'escalated', 'no_expiry'] as const;
export type ComplianceReminderState = (typeof COMPLIANCE_REMINDER_STATES)[number];

/**
 * supplier_360/compliance_requirement/ — docs §2.8. One unified list for both layers. Full CRUD
 * since 2026-10-05; `state` can also change as a side effect of uploading evidence or the
 * reminder scheduler.
 */
export type ComplianceRequirement = {
    id: number;
    entity: number;
    /** Null iff `layer === 'company_wide'`, set iff `layer === 'business_unit'` (server-enforced). */
    bu_qualification: number | null;
    layer: ComplianceLayer;
    name: string;
    kind: string;
    state: ComplianceRequirementState;
    /** "Valid from". */
    issued_at: string | null;
    /** "Valid until". */
    expires_at: string | null;
    /** Driven by a daily backend job off `expires_at` — purely informational to the frontend. */
    last_reminder_state: ComplianceReminderState;
    last_reminder_sent_at: string | null;
    /** The `ComplianceRequirementTemplate` (§2.8b) this was created from, if any. */
    created_from_template: number | null;
    /** The latest evidence's `verification_status` (§2.9, by `uploaded_at`) — `null` with no evidence. Read-only. */
    verification_status: VerificationStatus | null;
    /** The latest evidence's `name` — `null` with no evidence. Read-only. */
    latest_evidence_name: string | null;
    /**
     * The BU qualifications a company-wide record applies to (§2.8), nested. Always empty for a
     * business-unit record, and empty for a company-wide one until it is linked (`applies_to_ids`).
     */
    applies_to: BuQualification[];
};

// ── Compliance — evidence artifacts — §2.9 ──

export const COMPLIANCE_ARTIFACT_KINDS = [
    'certificate', 'declaration', 'insurance', 'permit', 'audit_report',
    'questionnaire', 'test_result', 'contract_annex', 'other',
] as const;
export type ComplianceArtifactKind = (typeof COMPLIANCE_ARTIFACT_KINDS)[number];

export const VERIFICATION_STATUSES = ['verified', 'self_attested', 'pending'] as const;
export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number];

/**
 * `supplier_360/compliance_artifact/` — §2.9. Evidence for one requirement. `name` is a display
 * name, independent of the uploaded file's own name.
 */
export type ComplianceArtifact = {
    id: number;
    entity: number;
    satisfies_requirement: number;
    kind: ComplianceArtifactKind;
    name: string;
    issuing_body: string;
    number: string;
    uploaded_by: string;
    /** Server-set. */
    uploaded_at: string;
    /** Bytes. */
    file_size: number | null;
    issued_at: string | null;
    expires_at: string | null;
    verification_status: VerificationStatus;
    /**
     * Storage URL of the uploaded document, `null` when there is none. Read it as a flag only —
     * open the file through `download-url/` (`complianceArtifactDownloadUrl`).
     */
    file: string | null;
};

/**
 * What the "Upload evidence" form sends (§2.9), as `multipart/form-data`. Creating one flips the
 * requirement to `met` server-side.
 */
export type ComplianceArtifactWrite = {
    file: File;
} & Pick<ComplianceArtifact,
    'entity' | 'satisfies_requirement' | 'kind' | 'name' | 'uploaded_by' | 'issued_at' | 'expires_at' | 'file_size'>;

/**
 * A row of `compliance_requirement/muix/grid/` (§2.8a) with its `bu_qualification` expanded through
 * `related_data` and flattened onto the row. The BU fields are absent for company-wide rows.
 */
/**
 * A requirement without `applies_to`, whose shape differs between the plain endpoint (nested) and
 * the MUIX grid (ids) — what code that takes either kind of row accepts (e.g. the edit dialog).
 */
export type ComplianceRequirementFields = Omit<ComplianceRequirement, 'applies_to'>;

export type ComplianceRequirementGridRow = ComplianceRequirementFields
    & Partial<Pick<BuQualification, 'region' | 'category' | 'qualification_status' | 'requalification_due'>>
    & {
        /** The MUIX serializer returns the M2M as plain `BUQualification` ids, not nested objects. */
        applies_to: number[];
        /**
         * `bu_qualification.business_unit.bu_name`, expanded two hops down through `related_data`
         * (2026-10-28, per the user) — the Layer column's "which BU" line. Absent for a company-wide
         * row (no `bu_qualification` to expand through at all).
         */
        bu_name?: string;
    };

/**
 * `supplier_360/compliance_requirement/kpi_summary/?entity=` — §2.8. The Compliance tab's 5 KPI
 * cells as real per-supplier totals (not page-scoped). All zeroes when the entity has no records.
 */
export type ComplianceKpiSummary = {
    records: number;
    company_wide: number;
    business_unit: number;
    met: number;
    /** `state=expiring`. */
    expiring_soon: number;
    /** `state=missing` + `state=expired`, collapsed into one cell. */
    unmet: number;
    /** `state=pending`. */
    pending_review: number;
};

/**
 * The writable fields the Add / Edit compliance record dialog sends (§2.8). `entity`, `layer` and
 * `name` are required on create; `bu_qualification` must be null for `company_wide` and set for
 * `business_unit` — a mismatch is a server-side `400`.
 */
export type ComplianceRequirementWrite = {
    entity: number;
    layer: ComplianceLayer;
    bu_qualification: number | null;
    name: string;
    kind: string;
    issued_at: string | null;
    expires_at: string | null;
    /**
     * The `BUQualification`s a company-wide record applies to (§2.8, read back as `applies_to`).
     * Only valid with `layer: 'company_wide'` — sent with a business-unit record it is a `400`.
     */
    applies_to_ids?: number[];
};

// ── Risk signals — §2.10 ──

/** Capitalised on the wire, unlike every other choice field — kept verbatim from the external feed (§2.10). */
export type RiskSignalImpactType = 'Business Risk' | 'Business Growth' | 'Business Change' | 'Governments' | 'World';

export type SupplierRiskSignal = {
    id: number;
    entity: number;
    external_doc_id: string;
    title: string;
    source_name: string;
    /** Unbounded, not format-validated. */
    source_url: string;
    /** ISO date. */
    published_at: string | null;
    /** 2-letter code, e.g. `"EN"`. */
    language: string;
    relevance_score: number;
    intensity: number;
    /** Free text, the feed's own taxonomy — e.g. `"Regulatory compliance Issues"`. */
    category: string;
    impact_type: RiskSignalImpactType;
    /** The sub-breakdown under a category on the Insights tab (§3.8). */
    event_type: string;
    event_type_category_title: string;
    event_type_category_id: string;
    sentiment: string;
    tone: string;
};

/**
 * `supplier_360/risk_signal/` filters (§2.10). `impact_type`, `category` and `language` accept a
 * single value (exact) or several (`__in`); `search` matches `title` only.
 */
export type SupplierRiskSignalFilters = {
    impact_type?: RiskSignalImpactType | RiskSignalImpactType[];
    category?: string | string[];
    language?: string | string[];
    search?: string;
};

export type SupplierRiskSignalListParams = SupplierRiskSignalFilters & {
    page: number;
    pageSize: number;
};

// ── Health scores — §2.11 ──

export type HealthScoreDomain = 'Financial' | 'Operational' | 'Compliance' | 'Cybersecurity' | 'ESG' | 'Regional' | 'Overall';

/**
 * Historical/append-only rows as of 2026-10-21 (merged from the former, SV-only
 * `ExternalHealthScore`) — `source` (an `InfoSource` id) discriminates who computed a given row.
 * `supplierHealthScoreUrl()` pins its request to our own deterministic source via `source__name`,
 * so every row this app actually fetches today is still exactly one of the original 6 domains —
 * `'Overall'` and other sources' rows exist in the API but aren't requested here.
 */
export type SupplierHealthScore = {
    id: number;
    entity: number;
    source: number;
    domain: HealthScoreDomain;
    score: number;
    delta_vs_prior_period: number;
    document_count: number;
    is_weakest: boolean;
    computed_at: string;
};

// ── Segmentation — §2.12 ──

export type SegmentationType = 'strategic' | 'leverage' | 'bottleneck' | 'routine';
export type SegmentationSource = 'algorithm' | 'manual';

export type SegmentationScore = {
    id: number;
    entity: number;
    business_unit: string;
    spend_value: number;
    supply_risk: number;
    switching_difficulty: number;
    substitutes: number;
    business_criticality: number;
    segment: SegmentationType;
    source: SegmentationSource;
    reason: string;
    scored_by: string;
    scored_at: string;
};

/**
 * The five 1–5 axes a score is made of — the write side of §2.12, shared by the first score
 * and by a re-score. `segment` is deliberately not in here: it is picked separately, since the
 * new design makes the quadrant the scorer's own call rather than something derived from the axes.
 */
export type SegmentationAxes = Pick<SegmentationScore,
    'spend_value' | 'supply_risk' | 'switching_difficulty' | 'substitutes' | 'business_criticality'>;

// ── Segmentation audit — §2.13 ──

export type SegmentationAuditEntry = {
    id: number;
    entity: number;
    from_segment: string;
    to_segment: string;
    changed_by: string;
    changed_at: string;
    reason: string;
};

// ── Spend summary & trend — §2.22 ──

export const SUPPLIER_SPEND_VIEW = {
    sourceSystem: 'source_system',
    category: 'category',
    businessUnit: 'business_unit',
} as const;

/** Which dimension the "Spend summary" breakdown is grouped by. All three come back in one call. */
export type SupplierSpendView = (typeof SUPPLIER_SPEND_VIEW)[keyof typeof SUPPLIER_SPEND_VIEW];

/** Segmented-control order — source system first. */
export const SUPPLIER_SPEND_VIEWS = [
    SUPPLIER_SPEND_VIEW.sourceSystem,
    SUPPLIER_SPEND_VIEW.category,
    SUPPLIER_SPEND_VIEW.businessUnit,
] as const;

export const SUPPLIER_SPEND_VIEW_LABEL: Record<SupplierSpendView, string> = {
    [SUPPLIER_SPEND_VIEW.sourceSystem]: 'Source system',
    [SUPPLIER_SPEND_VIEW.category]: 'Category',
    [SUPPLIER_SPEND_VIEW.businessUnit]: 'Business unit',
};

export type SupplierSpendSummaryRow = {
    name: string;
    spend: number;
    /** The trailing `"N more"` bucket folding everything past the top N — rendered de-emphasised. */
    muted: boolean;
};

/**
 * `supplier_360/entity/{id}/spend_summary/` — §2.22. Not date-scoped: `total` is all resolved
 * spend for the entity, regardless of the section subtitle's "last 12 months".
 */
export type SupplierSpendSummary = {
    total: number;
    views: Record<SupplierSpendView, SupplierSpendSummaryRow[]>;
};

/**
 * `supplier_360/entity/{id}/spend_trend/` — §2.22. `curr[i]`/`prev[i]` are the same calendar
 * month one year apart; `months` are ISO `YYYY-MM` (month names are left to the FE).
 *
 * The `yoy_*` trio is a *different* window from `curr`/`prev`: calendar year-to-date (Jan 1 →
 * today) vs the same elapsed span a year earlier, not the rolling trailing 12 months. Per §2.22
 * that calendar window is what the design's "YoY" badge means, so read these rather than
 * deriving a percentage from `curr`/`prev`.
 */
export type SupplierSpendTrend = {
    months: string[];
    curr: number[];
    prev: number[];
    /** Legend/series text for `curr`/`prev` — backend-owned copy, not hardcoded here (§2.22). */
    curr_label: string;
    prev_label: string;
    yoy_current_total: number;
    yoy_prior_total: number;
    /** `null` when `yoy_prior_total` is 0 — no meaningful % change from a zero base (§2.22). */
    yoy_pct: number | null;
    /** Hover text for the YoY badge — the badge's window differs from the chart's rolling one. */
    yoy_tooltip: string;
};

// ── KPI aggregates — §2.1 (per entity), §2.25 (per databag) ──

/**
 * `supplier_360/entity/{id}/kpi_summary/` — §2.1. The profile header's KPI strip in one call,
 * instead of reading one number out of `spend_summary` / the transactions grid / `health_score` /
 * `bu_qualification` each.
 *
 * Carries only the 4 pills with a real model behind them. "Active contracts", "On-time" and
 * "Open opportunities" are omitted rather than sent as a fabricated `0`, because nothing backs
 * them at all — the `contracts` app is a schema-less stub, the scored-KPI panel was never built,
 * and no `Opportunity` model exists (§2.1, §3.3, §3.5, §3.6).
 */
export type SupplierKpiSummary = {
    /** Cluster-wide `p_spend` sum — the same figure as `spend_summary.total`. `0`, never null. */
    spend: number;
    /** Row count of the very queryset the Transactions tab pages through. */
    transactions: number;
    /** Average of the entity's 6 health-score domains, rounded. `0` means *not yet computed*, not "no risk". */
    risk_score: number;
    /** `BUQualification` rows at `status=compliant`, out of `total_bus`. Both `0` when the entity has none. */
    compliant_bus: number;
    total_bus: number;
};

/**
 * `supplier_360/entity/registry_summary/?databag_id=` — §2.25. The registry's KPI strip in one
 * call. A pure aggregate over the whole databag: unlike the list endpoints' `count`, these
 * numbers do not move when a search or column filter is applied.
 */
export type SupplierRegistrySummary = {
    /** `status=confirmed` entities only — not every entity the registry table lists. */
    suppliers: number;
    /** Every `ClusterMember` for the databag, whatever its entity's status. */
    raw_rows: number;
    /** Distinct `ClusterMember.data_source_id`, nulls excluded. */
    sources: number;
    /**
     * Scoped to the `s_id`s Supplier 360 actually knows about (every `ClusterMember`'s own `s_id`),
     * not a bare databag-wide `p_spend` sum — that would also count
     * raw ERP rows nothing here has ever clustered. A plain number, unlike §2.1's string `spend`.
     */
    spend: number;
    /** The raw counts behind `resolved_pct`, for a "140 of 165" caption without a second call. */
    resolved_confirmed: number;
    resolved_total: number;
    /**
     * Review-queue completion rate: `confirmed / (confirmed + needs_review) * 100`, 1 decimal.
     * `rejected` counts on neither side. `null`, not `0`, when there is nothing to review yet —
     * so "no review queue" stays distinguishable from "0 % reviewed".
     */
    resolved_pct: number | null;
};
